<template>
  <v-dialog :model-value="modelValue" width="560" persistent @update:model-value="v => $emit('update:modelValue', v)">
    <v-card>
      <v-card-title>Link as shared day</v-card-title>
      <v-card-text>
        <v-skeleton-loader v-if="loading" type="list-item-two-line@2" />
        <v-alert v-else-if="loadError" type="error" density="compact">{{ loadError }}</v-alert>
        <template v-else>
          <p class="text-body-2 mb-3">
            {{ personName }} is also booked the same day on another gig. Linked, they get <strong>one offer</strong>
            and are paid <strong>one day</strong> (overtime on the combined hours), and each gig carries its share
            of the cost by hours worked. Each client is still billed from its own gig's quote.
          </p>
          <p v-if="!candidates.length" class="text-body-2 text-medium-emphasis" data-testid="no-same-day">
            No other draft bookings for this person on the same day.
          </p>
          <v-radio-group v-else v-model="pickedId" hide-details>
            <v-radio v-for="c in candidates" :key="c.booking.id" :value="c.booking.id" :data-testid="`link-candidate-${c.booking.id}`">
              <template #label>{{ c.gigName }} — {{ c.role }} ({{ c.when }})</template>
            </v-radio>
          </v-radio-group>
          <v-text-field
            v-if="candidates.length"
            v-model.number="rateAmount"
            type="number"
            min="0"
            :label="`Rate for the whole day (${booking.rateType === 'hourly' ? 'per hour' : 'per day'})`"
            hint="Starts at this booking's rate. Both bookings are repriced to it."
            persistent-hint
            class="mt-3"
          />
          <v-alert v-if="warning" type="warning" variant="tonal" density="compact" class="mt-3">{{ warning }}</v-alert>
          <v-alert v-if="submitError" type="error" density="compact" class="mt-3">{{ submitError }}</v-alert>
        </template>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn variant="text" @click="$emit('update:modelValue', false)">Cancel</v-btn>
        <v-btn color="primary" :disabled="!picked || saving" :loading="saving" data-testid="confirm-link" @click="submit">
          Link
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import type { Booking, Call, Position } from '@gigdriver/domain';
import { findSameDayBookings, validateSharedDayLink } from '@gigdriver/domain';
import { getGigCalls, getOrgGigs, getPosition } from '@gigdriver/client';
import { useSharedDaysStore } from '../stores/sharedDays';

interface Candidate {
  booking: Booking;
  gigName: string;
  role: string;
  when: string;
  position: Position;
}

const props = defineProps<{
  modelValue: boolean;
  orgId: string;
  booking: Booking;
  position: Position;
  // This gig's calls and the zone days are grouped in.
  calls: Call[];
  timeZone: string;
  personName: string;
}>();
const emit = defineEmits<{ (e: 'update:modelValue', v: boolean): void; (e: 'linked'): void }>();

const sharedStore = useSharedDaysStore();
const loading = ref(false);
const saving = ref(false);
const loadError = ref<string | null>(null);
const submitError = ref<string | null>(null);
const candidates = ref<Candidate[]>([]);
const pickedId = ref<string | null>(null);
const rateAmount = ref<number | undefined>(undefined);

const picked = computed(() => candidates.value.find(c => c.booking.id === pickedId.value) ?? null);
const warning = computed(() => {
  const c = picked.value;
  if (!c) return null;
  const invalid = validateSharedDayLink([props.booking, c.booking], { amount: rateAmount.value ?? 0, currency: 'USD' });
  if (invalid) return invalid;
  if (c.position.role !== props.position.role) {
    return `Different roles (${props.position.role} and ${c.position.role}). The day is priced on this booking's role.`;
  }
  return null;
});

watch(
  () => props.modelValue,
  async open => {
    if (!open) return;
    loading.value = true;
    loadError.value = null;
    submitError.value = null;
    pickedId.value = null;
    rateAmount.value = props.booking.rate?.amount ?? props.position.rate?.amount;
    try {
      const mine = props.calls.filter(c => props.booking.callIds.includes(c.id));
      const all = await sharedStore.personBookings(props.orgId, props.booking.personId);
      const others = all.filter(b => b.id !== props.booking.id && b.gigId !== props.booking.gigId);
      const gigGroups = new Map<string, Booking[]>();
      for (const b of others) gigGroups.set(b.gigId, [...(gigGroups.get(b.gigId) ?? []), b]);
      const gigs = await getOrgGigs(props.orgId);
      const withSpans = (
        await Promise.all(
          [...gigGroups.entries()].map(async ([gigId, bs]) => {
            const gigCalls = await getGigCalls(props.orgId, gigId);
            return bs.map(booking => ({ booking, spans: gigCalls.filter(c => booking.callIds.includes(c.id)) }));
          }),
        )
      ).flat();
      const same = findSameDayBookings(mine, withSpans, props.timeZone).filter(b => b.state === 'DRAFT');
      const next: Candidate[] = [];
      for (const b of same) {
        const position = await getPosition(props.orgId, b.gigId, b.positionId);
        if (!position) continue;
        const spans = withSpans.find(w => w.booking.id === b.id)?.spans ?? [];
        const first = spans[0]?.startTime;
        next.push({
          booking: b,
          position,
          gigName: gigs.find(g => g.id === b.gigId)?.name ?? 'Another gig',
          role: position.role,
          when: first ? first.toLocaleString([], { timeZone: props.timeZone, weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '',
        });
      }
      candidates.value = next;
      if (next.length === 1) pickedId.value = next[0].booking.id;
    } catch (err) {
      loadError.value = err instanceof Error ? err.message : 'Failed to look up this person’s other bookings';
    } finally {
      loading.value = false;
    }
  },
  // The dialog is mounted already open (v-if + v-model flip together).
  { immediate: true },
);

async function submit() {
  const c = picked.value;
  if (!c) return;
  const invalid = validateSharedDayLink([props.booking, c.booking], { amount: rateAmount.value ?? 0, currency: 'USD' });
  if (invalid) {
    submitError.value = invalid;
    return;
  }
  saving.value = true;
  submitError.value = null;
  try {
    await sharedStore.link({
      orgId: props.orgId,
      members: [props.booking, c.booking].map(b => ({ gigId: b.gigId, positionId: b.positionId, bookingId: b.id })),
      primaryBookingId: props.booking.id,
      rate: { amount: rateAmount.value as number, currency: props.booking.rate?.currency ?? 'USD' },
    });
    emit('linked');
    emit('update:modelValue', false);
  } catch (err) {
    submitError.value = err instanceof Error ? err.message : 'Failed to link';
  } finally {
    saving.value = false;
  }
}
</script>
